// frontend/src/components/QuotaLimitModal.jsx
import React from 'react';
import { ShieldAlert, ArrowUpRight, Mail, X } from 'lucide-react';
import { useLanguageStore } from '../store/useLanguageStore';

/**
 * Modal reutilizable para errores de límite de plan / feature no disponible.
 *
 * Props:
 *   visible    — boolean: controlar visibilidad
 *   onClose    — fn: cerrar el modal
 *   errorCode  — string: código del error (ERR_AI_DAILY_LIMIT, etc.)
 *   params     — object: params del error para interpolación
 */

// Códigos de error que este modal debe interceptar
export const QUOTA_ERROR_CODES = [
  'ERR_QUOTA_PIPELINE_EXCEEDED',
  'ERR_NO_ACTIVE_SUBSCRIPTION',
  'ERR_AI_DAILY_LIMIT',
  'ERR_WEBHOOKS_NOT_AVAILABLE',
  'ERR_SCRIPT_NOT_IN_PLAN',
  'ERR_SCRIPT_QUOTA_EXCEEDED',
  'ERR_FORCE_REQUIRES_ACTIVE',
];

export function isQuotaError(code) {
  if (!code) return false;
  return QUOTA_ERROR_CODES.includes(code) || code.startsWith('ERR_QUOTA_');
}

export default function QuotaLimitModal({ visible, onClose, errorCode, params = {} }) {
  const { t } = useLanguageStore();

  React.useEffect(() => {
    if (!visible) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [visible, onClose]);

  if (!visible) return null;

  // Determinar si es "límite alcanzado" vs "feature no disponible"
  const isFeatureGated = [
    'ERR_WEBHOOKS_NOT_AVAILABLE',
    'ERR_SCRIPT_NOT_IN_PLAN',
  ].includes(errorCode);

  const title = isFeatureGated
    ? (t('subscription.feature_not_available_title') || 'Función no disponible en tu plan')
    : (t('subscription.limit_reached_title') || 'Has alcanzado el límite de tu plan');

  // Mensaje contextual según el error
  const getMessage = () => {
    const key = `errors.${errorCode}`;
    let msg = t(key);

    if (msg && msg !== key) {
      // Interpolar params: {{used}}, {{limit}}, etc.
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          msg = msg.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v);
        });
      }
      return msg;
    }

    // Fallback genérico
    return isFeatureGated
      ? (t('subscription.upgrade_prompt') || 'Esta función está disponible en planes superiores.')
      : (t('subscription.upgrade_prompt') || 'Actualiza tu plan para continuar usando esta función.');
  };

  const handleViewPlans = () => {
    // Navegar a la sección de pricing en la landing
    window.open('https://datiaflow.com/#pricing', '_blank');
    onClose();
  };

  const handleContactSales = () => {
    window.location.href = 'mailto:support@varanext.com?subject=Upgrade%20Plan%20DatiaFlow';
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 pb-4 text-center relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-amber-50 flex items-center justify-center">
            <ShieldAlert className="w-7 h-7 text-amber-500" />
          </div>

          <h3 className="text-lg font-bold text-slate-800 mb-2">{title}</h3>
          <p className="text-sm text-slate-500 leading-relaxed">{getMessage()}</p>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleViewPlans}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors"
          >
            <ArrowUpRight className="w-4 h-4" />
            {t('subscription.view_plans') || 'Ver planes'}
          </button>
          <button
            onClick={handleContactSales}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
          >
            <Mail className="w-4 h-4" />
            {t('subscription.contact_sales') || 'Contactar ventas'}
          </button>
        </div>
      </div>
    </div>
  );
}